import TableColum from "../../models/tableColumn";
import TableColumnDataType from "../../models/tableColumnDataType";
import type { ConfigColumns } from "datatables.net";
import unitConverter from "../../util/units/unitConverter";
import numberHelper from "../../util/numberHelper";
import dateTimeHelper from "../../util/dateTimeHelper";
import stringHelper from "../../util/stringHelper";
import moment from "moment";
import TableManager from "../../models/tableManager";
import ProjectInfo from "../../models/projectInfo";
import NavigatorPosition from "../../models/navigatorPosition";
import Navigable from "../../models/navigable";
import Navigator from "../../models/navigator";

const updateScrollY = (
  height: number,
  gridRef: React.RefObject<HTMLDivElement>
) => {
  // adjust with some offset if needed (for headers, footers, margins)
  //const newHeight = height - 50;
  //const newHeight = height - 46; // best for non compact
  const newHeight = height - 32; // best for compact

  const wrapper = gridRef.current as any;
  if (wrapper) {
    const scrollBody = wrapper.querySelector(".dt-scroll-body");
    if (scrollBody) {
      scrollBody.style.height = newHeight + "px";
      scrollBody.style.maxHeight = newHeight + "px";
    }
  }
};

const getDataTablesColumnType = (tableColumn: TableColum) => {
  /*
        datatables data types
        "string"
        "num" (raw numbers)
        "num-fmt" (numbers with formatting, e.g., $1,000)
        "date" (basic date handling)
        "html" (for sorting HTML content as text)
    */

  if (tableColumn.dataType === TableColumnDataType.String) {
    return "string";
  } else if (tableColumn.dataType === TableColumnDataType.Number) {
    return "num";
  } else if (tableColumn.dataType === TableColumnDataType.Date) {
    return "date";
  }

  return "string";
};

const getColumnHeaderDisplayLabel = (
  tableColumn: TableColum,
  showUnitsInColumnHeaders: boolean
): string => {
  let columnHeaderDisplayLabel = tableColumn.displayName;
  const unit = unitConverter.getSymbol(tableColumn.unitMeasure);

  if (unit && showUnitsInColumnHeaders) {
    columnHeaderDisplayLabel += " " + unit;
  }

  return columnHeaderDisplayLabel;
};

const getConfigColumns = (tableColumns: TableColum[], project: ProjectInfo) => {
  const columns: ConfigColumns[] = tableColumns.map((c) => {
    return {
      title: getColumnHeaderDisplayLabel(
        c,
        project.showUnitsInTableColumnHeaders
      ),
      data: c.columnName,
      className: "dt-left",
      type: getDataTablesColumnType(c),
      render: function (data, type, row) {
        const dataFixed = row[c.columnName];
        const convertedData = getCellValue(
          dataFixed,
          c.unitMeasure,
          c.decimals,
          c.dataType
        );
        return convertedData;
      },
    };
  });
  return columns;
};

const getCellValue = (
  cellValue: any,
  unit: any,
  decimals: any,
  dataType: any
) => {
  if (cellValue === undefined || cellValue === null) {
    return cellValue;
  }

  cellValue = unitConverter.convertValue(cellValue, unit);

  if (decimals !== -1 && dataType === "Number") {
    cellValue = numberHelper.numberToNDigits(cellValue, decimals);
  }

  const isDateTimeValue = dateTimeHelper.isDateTimeValue(cellValue);
  if (isDateTimeValue) {
    const dateValue = new Date(cellValue);
    cellValue = moment(dateValue).format(dateTimeHelper.dateTimeFormat);
  }

  const isDateOnlyValue = dateTimeHelper.isDateOnlyValue(cellValue);
  if (isDateOnlyValue) {
    const dateValue = new Date(cellValue);
    cellValue = moment(dateValue).format(dateTimeHelper.dateFormat);
  }

  cellValue = stringHelper.replaceConsecutiveSpaces(
    cellValue.toString().trim()
  );

  return cellValue;
};

const onRowSelected = async (
  rowData: any[],
  tableManager: TableManager,
  navigator: Navigator,
  navigable: Navigable,
  project: ProjectInfo
) => {
  const tableHelperData = tableManager.getTableHelperData(rowData);
  if (tableHelperData) {
    const navigatorPoistion = new NavigatorPosition();
    navigatorPoistion.idProject = project.idProject;
    navigatorPoistion.idRoad = tableHelperData.idRoad;
    navigatorPoistion.idSession = tableHelperData.idSession;
    navigatorPoistion.distance = tableHelperData.startDistance;

    await navigator.move(navigatorPoistion, navigable);
  }
};

const tableCommon = {
  updateScrollY,
  getConfigColumns,
  onRowSelected,
};

export default tableCommon;
